import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { rxResource, toObservable } from '@angular/core/rxjs-interop';
import { EventDto, EventService } from '@millionshow/api-client';
import {
  Icon,
  PageModalContainer,
  SafeHtmlPipe,
  Segment,
  SegmentOption,
} from '@millionshow/client-libs';
import { formatDateRange } from '../../utils/date-formatter';
import { injectParams } from 'ngxtension/inject-params';
import FaqComponent from './components/faq/faq.component';
import { RouterLink } from '@angular/router';
import { NotFoundComponent } from '../../components/not-found/not-found.component';
import { KakaoLoaderService } from '../../services/kakao-loader.service';
import { SeoService } from '../../services/seo.service';

declare const kakao: any;

@Component({
  selector: 'app-event-detail-page',
  imports: [
    CommonModule,
    Icon,
    SafeHtmlPipe,
    Segment,
    FaqComponent,
    PageModalContainer,
    RouterLink,
    NotFoundComponent,
  ],
  templateUrl: './event-detail.page.html',
})
export default class EventDetailPage {
  private readonly eventService = inject<EventService>(EventService);
  private readonly kakaoLoader = inject(KakaoLoaderService);
  private readonly seoService = inject(SeoService);

  readonly id = injectParams('id');

  formatDateRange = formatDateRange;

  $event = rxResource({
    stream: () => {
      return this.eventService.eventControllerFindByIdV1({
        id: this.id() ?? '',
      });
    },
  });

  data = computed(() => this.$event.value() as EventDto | undefined);
  loading = computed(() => this.$event.isLoading());

  /** 행사가 종료/마감되었는지 여부 (ONGOING이 아니면 마감) */
  isEventExpired = computed(() => {
    const event = this.data();
    if (!event) return true;
    return event.status !== 'ONGOING';
  });

  segment = signal<string>('details');
  segment$ = toObservable(this.segment);

  segmentOptions: SegmentOption[] = [
    { label: '상세 정보', value: 'details' },
    { label: '장소 안내', value: 'venue' },
    { label: 'FAQ', value: 'faq' },
    { label: '이벤트', value: 'events' },
    { label: '유의사항', value: 'notice' },
  ];

  private map: any;
  private geocoder: any;

  // 시간 포맷 (HH:mm)
  formatTime(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }

  // 입장마감 시간 계산 (종료 30분 전)
  getLastEntryTime(endDateStr: string): string {
    const endDate = new Date(endDateStr);
    endDate.setMinutes(endDate.getMinutes() - 30);
    return endDate.toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }

  // 시간 범위 문자열 생성
  formatTimeRange(startAt: string, endAt: string): string {
    const startTime = this.formatTime(startAt);
    const endTime = this.formatTime(endAt);
    const lastEntry = this.getLastEntryTime(endAt);
    return `${startTime} ~ ${endTime} (입장마감 ${lastEntry})`;
  }

  constructor() {
    effect(() => {
      const event = this.data();

      if (!event) return;

      const description =
        [
          this.stripHtml(event.details || ''),
          event.place?.name,
          event.place?.address,
        ]
          .filter(Boolean)
          .join(' ')
          .slice(0, 140) || '밀리언쇼 행사 상세 정보를 확인해보세요.';

      this.seoService.set({
        title: event.title || '행사 상세',
        description,
        path: `/event/${this.id()}`,
        image: event.thumbnail || undefined,
      });
    });

    this.segment$.subscribe(segment => {
      if (segment === 'venue' && this.data()) {
        setTimeout(() => this.initMap(), 300);
      }
    });
  }

  private async loadKakaoMaps(): Promise<void> {
    await this.kakaoLoader.load();
  }

  private async initMap() {
    const event = this.data();
    if (!event || !event.place?.address) return;

    const containers = document.querySelectorAll('.kakao-map-target');
    let container: HTMLElement | null = null;

    for (let i = 0; i < containers.length; i++) {
      const el = containers[i] as HTMLElement;
      if (el.offsetParent !== null) {
        container = el;
        break;
      }
    }

    if (!container) return;

    await this.loadKakaoMaps();

    this.geocoder = new kakao.maps.services.Geocoder();

    // 쉼표가 있으면 앞부분(기본 주소)만 사용
    const searchAddress = event.place.address.split(',')[0].trim();

    this.geocoder.addressSearch(searchAddress, (result: any, status: any) => {
      if (status === kakao.maps.services.Status.OK) {
        const coords = new kakao.maps.LatLng(result[0].y, result[0].x);

        const mapOption = {
          center: coords,
          level: 3,
        };

        this.map = new kakao.maps.Map(container, mapOption);

        const marker = new kakao.maps.Marker({
          map: this.map,
          position: coords,
        });

        const infowindow = new kakao.maps.InfoWindow({
          content: `
              <div style="
                font-size: 14px;
                font-weight: 600;
                text-align: center;
                padding: 8px 12px;
                min-width: 150px;
                width: 100%;
                ">
                  ${event.place.name}
              </div>
              `,
        });

        infowindow.open(this.map, marker);

        this.map.setCenter(coords);
      }
    });
  }

  openKakaoMapDirections() {
    const event = this.data();
    if (!event || !event.place?.name) return;

    const name = encodeURIComponent(event.place.name);
    const address = event.place.address
      ? encodeURIComponent(event.place.address)
      : '';

    window.open(`https://map.kakao.com/link/to/${name},${address}`, '_blank');
  }

  openKakaoMapDetail() {
    const event = this.data();
    if (!event || !event.place?.name) return;

    const name = encodeURIComponent(event.place.name);

    window.open(`https://map.kakao.com/link/search/${name}`, '_blank');
  }

  private stripHtml(html: string): string {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '');
  }
}
